import type { ProcessCellForExportParams } from 'ag-grid-community';
import { OrderType } from 'apiConnector/secondary-market/batch/model';
import { Sales } from 'apiConnector/secondary-market/shared/clients';
import {
  processCellFromClipboardWithShowError,
  useSimpleFormatMessage,
} from 'components/secondaryMarket/shared/Clipboard/clipboard';
import { translateDateOnly } from 'components/secondaryMarket/shared/Clipboard/parse-date-only';
import { translateEliotCodeIntoSaleCounterpart } from 'components/secondaryMarket/shared/Clipboard/parse-eliot-code';
import { translateNumber } from 'components/secondaryMarket/shared/Clipboard/parse-number';
import { AsyncTranslators, SyncTranslators } from 'components/secondaryMarket/shared/Clipboard/types';
import { EditableFieldInfo } from 'components/shared/Blotter/Grid/blotter.model';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { useMemo } from 'react';
import { translateColumnPossibleValues } from 'routes/secondary-market-batch/grid/hooks/clipboard/parse-enum';
import { translateSelectedSale } from 'routes/secondary-market-batch/grid/hooks/clipboard/parse-selected-sale';
import { CpOrderSettlementType } from 'state/secondaryMarket/cpOrder/referential/client/client.model';
import { secondaryMarketBatchActions } from 'state/workspaces/secondary-market/batch/actions';
import type { RowData, SecondaryBatchColumnDefinitionContext } from 'state/workspaces/secondary-market/batch/model';

import { translateSettlementType } from './parse-settlement-types';

export function useCpOrderTranslators() {
  const asyncTranslators: AsyncTranslators<RowData, 'client' | 'custodian'> = {
    client: translateEliotCodeIntoSaleCounterpart,
    custodian: translateEliotCodeIntoSaleCounterpart,
  };
  const syncTranslators: SyncTranslators<
    RowData,
    | 'settlementDate'
    | 'valuationDate'
    | 'settlementType'
    | 'quantity'
    | 'tradingPrice'
    | 'tradeWay'
    | 'tradeDateType'
    | 'selectedSale'
  > = {
    settlementDate: translateDateOnly,
    valuationDate: translateDateOnly,
    settlementType: translateSettlementType,
    quantity: translateNumber,
    tradingPrice: translateNumber,
    tradeWay: translateColumnPossibleValues,
    tradeDateType: translateColumnPossibleValues,
    selectedSale: translateSelectedSale,
  };

  return { syncTranslators, asyncTranslators };
}

export const processCellForClipboard = (params: ProcessCellForExportParams) => {
  const colId = params.column?.getColId();
  if (colId === 'client' || colId === 'custodian') {
    return (params.value as ISalesCounterpart).eliotCode;
  }
  if (colId === 'selectedSale') {
    return (params.value as Sales).firstName;
  }
  if (colId === 'settlementType') {
    return (params.value as CpOrderSettlementType).settlementType;
  }
  return params.value;
};

export function useProcessCellFromClipboard(
  orderType: OrderType,
  getRowId: (row: RowData) => string,
  callbacks?: {
    onCellPastePending?: (cell: { rowId: string; field: keyof RowData }) => void;
    onCellPasteSuccess?: (cell: { rowId: string; field: keyof RowData }) => void;
    onCellPasteFailure?: (cell: { rowId: string; field: keyof RowData }) => void;
  },
) {
  const dispatch = useThunkDispatch();
  const simpleFormatMessage = useSimpleFormatMessage();
  const { syncTranslators, asyncTranslators } = useCpOrderTranslators();

  const getMetadata = (columnContext: unknown, data: RowData) => {
    const metadataId = (columnContext as SecondaryBatchColumnDefinitionContext)?.metadataId;
    return metadataId ? data?.metadata?.[metadataId] : columnContext;
  };

  const result = useMemo(() => {
    const setUserFeedbackWithCell =
      ({ rowId, field }: { rowId: string; field: keyof RowData }) =>
      (state: EditableFieldInfo) => {
        dispatch(secondaryMarketBatchActions.setCellEditableInfo(orderType, rowId, field, state));
      };

    return processCellFromClipboardWithShowError<
      RowData,
      | 'settlementDate'
      | 'valuationDate'
      | 'settlementType'
      | 'quantity'
      | 'tradingPrice'
      | 'tradeWay'
      | 'tradeDateType'
      | 'selectedSale',
      'client' | 'custodian'
    >({
      getRowId,
      simpleFormatMessage,
      onCellPastePending: callbacks?.onCellPastePending,
      onCellPasteSuccess: callbacks?.onCellPasteSuccess,
      onCellPasteFailure: callbacks?.onCellPasteFailure,
      setUserFeedbackWithCell,
      asyncTranslators,
      syncTranslators,
      getMetadata,
    });
  }, [callbacks, getRowId, simpleFormatMessage, dispatch, orderType, asyncTranslators, syncTranslators]);
  return result;
}
